// src/pages/UpgradePage.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { getAllCattle } from '../utils/cattleService';

// REPLACE with your Razorpay Key ID from Razorpay Dashboard
const RAZORPAY_KEY_ID = 'rzp_live_SCZaqXZhEVjdnt';
const PLAN_CURRENCY = 'INR';

export default function UpgradePage() {
  const { currentUser, userPlan, activatePaidPlan, isPaid } = useAuth();
  const [loading, setLoading] = useState(false);
  const [recordCount, setRecordCount] = useState(0);
  const [amount, setAmount] = useState(12000);
  const [additionLimit, setAdditionLimit] = useState(2400);
  const [calculating, setCalculating] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    async function calculatePrice() {
      if (!currentUser?.uid) return;
      
      setCalculating(true);
      try {
        console.log('UpgradePage: fetching all cattle for', currentUser.uid);
        const records = await getAllCattle(currentUser.uid);
        const count = records.length;
        console.log('UpgradePage: count received', count);
        
        setRecordCount(count);
        
        let calculatedAmount = 12000;
        let calculatedLimit = 2400;

        if (count > 0) {
          // Logic: total number of records * 5 and round up to nearest 500 range (price not less than 2000)
          calculatedAmount = Math.max(2000, Math.ceil((count * 5) / 500) * 500);
          // Limit: 25% of records, minimum 100
          calculatedLimit = Math.max(100, Math.floor(count * 0.25));
        }
        
        console.log('UpgradePage: final calculated amount', calculatedAmount);
        setAmount(calculatedAmount);
        setAdditionLimit(calculatedLimit);
      } catch (err) {
        console.error('Error calculating price:', err);
        // Fallback to default
        setAmount(12000);
        setAdditionLimit(2400);
      } finally {
        setCalculating(false);
      }
    }

    calculatePrice();
  }, [currentUser]);

  function loadRazorpay() {
    return new Promise((resolve) => {
      if (window.Razorpay) { resolve(true); return; }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  }

  async function handleUpgrade() {
    setLoading(true);
    const ok = await loadRazorpay();
    if (!ok) { toast.error('Failed to load payment gateway. Check internet.'); setLoading(false); return; }

    const options = {
      key: RAZORPAY_KEY_ID,
      amount: amount * 100, // Amount in paise
      currency: PLAN_CURRENCY,
      name: 'Gaushala Manager',
      description: `Pro Plan - 1 Year Subscription (${recordCount} records)`,
      image: 'https://i.imgur.com/kNKZaUN.png',
      handler: async function (response) {
        const paymentId = response.razorpay_payment_id;
        await activatePaidPlan(paymentId, additionLimit);
        toast.success('🎉 Payment successful! Pro plan activated for 1 year.');
        navigate('/');
      },
      prefill: {
        email: currentUser?.email || '',
        name: userPlan?.name || '',
      },
      notes: {
        userId: currentUser?.uid,
        plan: 'annual',
        recordCount: recordCount,
        additionLimit: additionLimit,
      },
      theme: { color: '#2e7d32' },
      modal: {
        ondismiss: () => { setLoading(false); }
      }
    };

    const rzp = new window.Razorpay(options);
    rzp.on('payment.failed', function(response) {
      toast.error('Payment failed: ' + response.error.description);
      setLoading(false);
    });
    rzp.open();
    setLoading(false);
  }

  const renewalDate = userPlan?.renewalDate ? new Date(userPlan.renewalDate).toLocaleDateString('en-IN') : null;

  return (
    <div className="page-container" style={{ maxWidth: 700 }}>
      <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1b5e20', marginBottom: 6 }}>⭐ Upgrade to Pro</h1>
      <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 28 }}>Unlock unlimited access to all features</p>

      {isPaid && (
        <div style={{ background: '#dcfce7', border: '1px solid #86efac', borderRadius: 10, padding: 20, marginBottom: 24, textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>✅</div>
          <h2 style={{ fontWeight: 800, color: '#166534', marginBottom: 4 }}>You're on Pro Plan!</h2>
          <p style={{ color: '#15803d' }}>Your subscription renews on <strong>{renewalDate}</strong></p>
          <p style={{ color: '#6b7280', fontSize: 13, marginTop: 6 }}>Payment ID: {userPlan?.paymentId}</p>
          <div style={{ marginTop: 12, background: 'white', borderRadius: 8, padding: '8px 12px', fontSize: 14, border: '1px solid #86efac', display: 'inline-block' }}>
            New additions: <strong>{userPlan?.addCount || 0} / {userPlan?.additionLimit}</strong> used
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 28 }}>
        {/* Free Plan */}
        <div className="card" style={{ border: '2px solid #d1d5db' }}>
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>🆓</div>
            <h2 style={{ fontWeight: 800, fontSize: 20 }}>Free Plan</h2>
            <p style={{ fontSize: 28, fontWeight: 800, color: '#1b5e20', margin: '8px 0' }}>₹0</p>
            <p style={{ color: '#6b7280', fontSize: 13 }}>Forever free</p>
          </div>
          <ul style={{ listStyle: 'none', fontSize: 14, lineHeight: 2 }}>
            <li>✅ Import Excel (unlimited)</li>
            <li>✅ View all records</li>
            <li>✅ Export Excel</li>
            <li>✅ Search by tag</li>
            <li>⚠️ Add only 5 records</li>
            <li>⚠️ Edit only 5 records</li>
            <li>❌ No priority support</li>
          </ul>
          {!isPaid && (
            <div style={{ marginTop: 16, background: '#fef3c7', borderRadius: 7, padding: '8px 12px', fontSize: 13, color: '#92400e' }}>
              Adds: {userPlan?.addCount || 0}/5 used &nbsp;|&nbsp; Edits: {userPlan?.editCount || 0}/5 used
            </div>
          )}
        </div>

        {/* Pro Plan */}
        <div className="card" style={{ border: '2px solid #2e7d32', position: 'relative', overflow: 'hidden' }}>
          <div style={{
            position: 'absolute', top: 16, right: -20, background: '#2e7d32', color: 'white',
            padding: '4px 40px', fontSize: 12, fontWeight: 700, transform: 'rotate(45deg) translateX(10px)'
          }}>POPULAR</div>
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>⭐</div>
            <h2 style={{ fontWeight: 800, fontSize: 20 }}>Pro Plan</h2>
            {calculating ? (
              <div style={{ margin: '8px 0' }}>
                <div className="spinner" style={{ margin: '0 auto', width: 20, height: 20 }}></div>
                <p style={{ color: '#6b7280', fontSize: 13, marginTop: 4 }}>Calculating price...</p>
              </div>
            ) : (
              <>
                <p style={{ fontSize: 28, fontWeight: 800, color: '#1b5e20', margin: '8px 0' }}>₹{amount.toLocaleString('en-IN')}</p>
                <p style={{ color: '#6b7280', fontSize: 13 }}>
                  {recordCount > 0 ? `per year for ${recordCount} records` : 'per year for new users'}
                </p>
                {recordCount === 0 && (
                  <p style={{ color: '#d97706', fontSize: 12, fontWeight: 600, marginTop: 4 }}>
                    ⚠️ Please import first to calculate price
                  </p>
                )}
              </>
            )}
          </div>
          <ul style={{ listStyle: 'none', fontSize: 14, lineHeight: 2 }}>
            <li>✅ Import Excel (unlimited)</li>
            <li>✅ View all records</li>
            <li>✅ Export Excel</li>
            <li>✅ Search by tag</li>
            <li>✅ <strong>Add {additionLimit.toLocaleString('en-IN')} new records</strong></li>
            <li>✅ <strong>Unlimited edit records</strong></li>
            <li>✅ Priority support</li>
          </ul>
          {!isPaid && (
            <button
              className="btn btn-primary"
              style={{ width: '100%', justifyContent: 'center', marginTop: 20, fontSize: 16, padding: 14 }}
              onClick={handleUpgrade}
              disabled={loading || calculating}
            >
              {loading ? '⏳ Processing...' : calculating ? 'Calculating...' : `💳 Pay ₹${amount.toLocaleString('en-IN')} via Razorpay`}
            </button>
          )}
        </div>
      </div>

      <div className="card" style={{ background: '#f0fdf4', border: '1px solid #86efac' }}>
        <h3 style={{ fontWeight: 700, marginBottom: 12, color: '#166534' }}>🔒 Secure Payment by Razorpay</h3>
        <p style={{ fontSize: 14, color: '#374151', lineHeight: 1.7 }}>
          Payments are processed securely through Razorpay. We accept UPI, Credit/Debit Cards, Net Banking, and Wallets.
          Your subscription will be active for <strong>1 year</strong> from the date of payment.
          After renewal date passes, the account automatically reverts to Free plan.
        </p>
        <div style={{ display: 'flex', gap: 20, marginTop: 14, flexWrap: 'wrap' }}>
          {['💳 Cards', '📱 UPI', '🏦 Net Banking', '👛 Wallets'].map(m => (
            <span key={m} style={{ background: 'white', border: '1px solid #86efac', borderRadius: 6, padding: '4px 12px', fontSize: 13 }}>{m}</span>
          ))}
        </div>
      </div>
    </div>
  );
}
